import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import {
  ProductStockAdjustment,
  ProductStockAdjustmentContext,
  STOCK_ADJUSTMENT_REASON_OTHER,
  StockAdjustmentDirection,
  stockAdjustmentReasonOptions,
  StockAdjustmentRequest,
  StockShortageError,
} from '../../production-inventory.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmationModalComponent } from '../../../common/components/confirmation-modal/confirmation-modal.component';
import {
  absDecimalString,
  decimalSign,
  formatDecimalQuantity,
  roundHalfUp,
  SnackbarAlertStatus,
  toNumberOrNull,
} from '../../../utils';

/** Whether the number in the box is boxes or loose units. */
export enum QuantityMode {
  BOXES = 'boxes',
  UNITS = 'units',
}

/** Positive numbers only. The sign comes from the Add/Remove toggle, never from typing. */
function positiveQuantityValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;

  const quantity = toNumberOrNull(value);
  if (quantity === null) return { quantityInvalid: true };
  if (quantity <= 0) return { quantityNotPositive: true };

  return null;
}

@Component({
  selector: 'app-product-stock-adjustment-form',
  standalone: false,
  templateUrl: './product-stock-adjustment-form.component.html',
  styleUrl: './product-stock-adjustment-form.component.scss',
})
export class ProductStockAdjustmentFormComponent implements OnInit, OnDestroy {
  /** The batch and go-down being adjusted. Fixed: the form never picks the batch. */
  @Input({ required: true }) context!: ProductStockAdjustmentContext;

  @Output() saved: EventEmitter<ProductStockAdjustment> = new EventEmitter<ProductStockAdjustment>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  protected adjustmentForm!: FormGroup;
  protected isSubmitting: boolean = false;

  /** Server said the removal exceeds stock. Its numbers are the whole point: show them. */
  protected shortageError: StockShortageError | null = null;
  protected quantityError: string | null = null;
  protected remarksError: string | null = null;
  protected productionEntryError: string | null = null;
  protected warehouseError: string | null = null;
  protected submitError: any = null;
  protected isForbidden: boolean = false;

  protected readonly Direction = StockAdjustmentDirection;
  protected readonly Mode = QuantityMode;

  private submitSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private productionInventoryService: ProductionInventoryService,
    private snackbarService: SnackbarService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.adjustmentForm = this.fb.group({
      direction: [StockAdjustmentDirection.ADD, Validators.required],
      // The floor counts in boxes, so that is the default. A packaging with no nos has
      // no box to count, which leaves units as the only workable entry.
      mode: [this.canUseBoxes ? QuantityMode.BOXES : QuantityMode.UNITS, Validators.required],
      quantity: [null, [Validators.required, positiveQuantityValidator]],
      reason: [null, Validators.required],
      remarks: [''],
      // Only asked for when the caller could not supply it. One batch can sit in several
      // warehouses, so this is never inferred from the batch.
      warehouse: [this.context.container_id, this.needsWarehousePicker ? Validators.required : []],
    });

    // The server requires remarks only for "other", so mirror that rather than
    // demanding an explanation for every adjustment.
    this.reasonControl.valueChanges.subscribe(() => this.applyRemarksRequirement());
    this.applyRemarksRequirement();
  }

  protected get directionControl(): AbstractControl {
    return this.adjustmentForm.get('direction')!;
  }

  protected get modeControl(): AbstractControl {
    return this.adjustmentForm.get('mode')!;
  }

  protected get quantityControl(): AbstractControl {
    return this.adjustmentForm.get('quantity')!;
  }

  protected get reasonControl(): AbstractControl {
    return this.adjustmentForm.get('reason')!;
  }

  protected get remarksControl(): AbstractControl {
    return this.adjustmentForm.get('remarks')!;
  }

  protected get warehouseControl(): AbstractControl {
    return this.adjustmentForm.get('warehouse')!;
  }

  protected get isRemoving(): boolean {
    return this.directionControl.value === StockAdjustmentDirection.REMOVE;
  }

  protected get reasonOptions() {
    return stockAdjustmentReasonOptions(this.context.scope);
  }

  /** "Factory" or "Warehouse", for every label that names the go-down. */
  protected get containerLabel(): string {
    return this.context.scope === 'factory' ? 'Factory' : 'Warehouse';
  }

  protected get needsWarehousePicker(): boolean {
    return this.context.scope === 'warehouse' && !this.context.container_id;
  }

  /** The name to print, which is only known once a warehouse has been chosen. */
  protected get containerName(): string {
    if (this.context.container_name) return this.context.container_name;

    const chosen = (this.context.warehouseOptions ?? []).find(w => w.id === this.warehouseControl?.value);
    return chosen?.name ?? 'the selected warehouse';
  }

  protected get isBoxMode(): boolean {
    return this.modeControl.value === QuantityMode.BOXES;
  }

  protected get canUseBoxes(): boolean {
    return (this.context?.nos ?? 0) > 0;
  }

  protected get isRemarksRequired(): boolean {
    return this.reasonControl.value === STOCK_ADJUSTMENT_REASON_OTHER;
  }

  /**
   * The API takes units. The floor counts boxes. This is the one multiplication the admin
   * must never be asked to do by hand, so it happens here and is shown back to them.
   */
  protected get unitsEntered(): number | null {
    const entered = toNumberOrNull(this.quantityControl.value);
    if (entered === null || entered <= 0) return null;

    return this.isBoxMode ? roundHalfUp(entered * this.context.nos, 2) : roundHalfUp(entered, 2);
  }

  /** "3 boxes = 72 units", the line under the input. */
  protected get conversionLabel(): string | null {
    const units = this.unitsEntered;
    if (units === null) return null;

    if (!this.isBoxMode) return `${formatDecimalQuantity(String(units))} units`;

    const boxes = toNumberOrNull(this.quantityControl.value)!;
    return `${formatDecimalQuantity(String(boxes))} boxes = ${formatDecimalQuantity(String(units))} units`;
  }

  protected get batchBalanceBefore(): number | null {
    return toNumberOrNull(this.context?.batch_units_available);
  }

  protected get containerBalanceBefore(): number | null {
    return toNumberOrNull(this.context?.container_units_available);
  }

  /** The resulting batch balance, recomputed as they type. */
  protected get batchBalanceAfter(): number | null {
    return this.balanceAfter(this.batchBalanceBefore);
  }

  protected get containerBalanceAfter(): number | null {
    return this.balanceAfter(this.containerBalanceBefore);
  }

  private balanceAfter(before: number | null): number | null {
    const units = this.unitsEntered;
    if (before === null || units === null) return null;

    return roundHalfUp(this.isRemoving ? before - units : before + units, 2);
  }

  /** "240 units now, 288 after". Null until there is a quantity to apply. */
  protected get batchBalanceLabel(): string | null {
    const before = this.batchBalanceBefore;
    if (before === null) return null;

    const after = this.batchBalanceAfter;
    const now = `${formatDecimalQuantity(String(before))} units now`;

    return after === null ? now : `${now}, ${formatDecimalQuantity(String(after))} after`;
  }

  protected setDirection(direction: StockAdjustmentDirection): void {
    this.directionControl.setValue(direction);
    // A shortage was about the previous direction and quantity; it no longer applies.
    this.shortageError = null;
    this.quantityError = null;
  }

  protected setMode(mode: QuantityMode): void {
    if (mode === QuantityMode.BOXES && !this.canUseBoxes) return;

    this.modeControl.setValue(mode);
    this.quantityControl.reset();
    this.shortageError = null;
    this.quantityError = null;
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  /**
   * One adjustment moves two balances, and there is no update or delete route to walk it
   * back, so both consequences and the permanence are stated before anything is posted.
   */
  protected onSubmit(): void {
    this.adjustmentForm.markAllAsTouched();
    if (this.adjustmentForm.invalid || this.isSubmitting) return;

    const ref = this.modalService.open(ConfirmationModalComponent, { backdrop: 'static', keyboard: false });

    ref.componentInstance.title =
      this.context.scope === 'factory'
        ? `Adjust batch ${this.context.batch_number}?`
        : `Adjust batch ${this.context.batch_number} at ${this.containerName}?`;
    ref.componentInstance.message = this.buildConfirmationMessage();
    ref.componentInstance.warning =
      'This cannot be edited or deleted afterwards. A wrong adjustment is undone by posting the opposite one, and both stay in the ledger.';
    ref.componentInstance.confirmText = this.isRemoving ? 'Remove stock' : 'Add stock';
    ref.componentInstance.confirmClass = this.isRemoving ? 'btn-danger' : 'btn-primary';
    ref.componentInstance.icon = 'bi-sliders';

    ref.result.then(
      () => this.postAdjustment(),
      () => {} // dismissed
    );
  }

  private buildConfirmationMessage(): string {
    const units = formatDecimalQuantity(String(this.unitsEntered));
    const movement = this.isRemoving ? 'removed from' : 'added to';
    const where = this.context.scope === 'factory' ? ` at ${this.containerName}` : '';
    const opening = `${units} units are ${movement} this batch${where}.`;

    const total =
      this.context.scope === 'factory'
        ? "The product's total factory stock there"
        : "The product's total stock in this warehouse";

    const before = this.containerBalanceBefore;
    const after = this.containerBalanceAfter;
    if (before === null || after === null) {
      return `${opening} ${total} moves by the same amount.`;
    }

    return (
      `${opening} ${total} moves by the same amount, from ` +
      `${formatDecimalQuantity(String(before))} to ${formatDecimalQuantity(String(after))}.`
    );
  }

  private postAdjustment(): void {
    this.clearServerErrors();
    this.isSubmitting = true;

    this.submitSubscription = this.productionInventoryService
      .createStockAdjustment(this.context.scope, this.buildRequest())
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (adjustment: ProductStockAdjustment) => {
          this.snackbarService.show(this.buildSuccessMessage(adjustment), SnackbarAlertStatus.SUCCESS, 6000);
          this.saved.emit(adjustment);
        },
        error: (error: HttpErrorResponse) => this.handleSubmitError(error),
      });
  }

  /** The toggle supplies the sign; the input only ever holds a positive number. */
  private buildRequest(): StockAdjustmentRequest {
    const magnitude = String(this.unitsEntered);

    const reqBody: StockAdjustmentRequest = {
      production_entry: this.context.production_entry,
      quantity: this.isRemoving ? `-${magnitude}` : magnitude,
      reason: this.reasonControl.value,
    };

    // Only the warehouse endpoint takes a container: the factory one derives it from the
    // batch and rejects the field as read-only.
    if (this.context.scope === 'warehouse') {
      reqBody.warehouse = this.warehouseControl.value;
    }

    const trimmedRemarks = (this.remarksControl.value ?? '').trim();
    if (trimmedRemarks) {
      reqBody.remarks = trimmedRemarks;
    }

    return reqBody;
  }

  /** Balances come from the response, never from arithmetic on this side. */
  private buildSuccessMessage(adjustment: ProductStockAdjustment): string {
    const verb = decimalSign(adjustment.quantity) < 0 ? 'Removed' : 'Added';
    const amount = formatDecimalQuantity(absDecimalString(adjustment.quantity));
    const batchBalance = formatDecimalQuantity(adjustment.batch_units_available_after);
    const containerBalance = formatDecimalQuantity(adjustment.container_units_available_after);

    return (
      `${verb} ${amount} units. Batch ${adjustment.batch_number} is now ${batchBalance} units, ` +
      `and ${adjustment.product_name} at ${adjustment.container_name} is ${containerBalance}.`
    );
  }

  /**
   * The three 400s do not share a shape: `quantity` arrives as a string[] from the zero
   * check and as a plain string from the shortage check, and `remarks` and
   * `production_entry` arrive as arrays. Normalising here is what keeps "[object Object]"
   * off the form.
   */
  private handleSubmitError(error: HttpErrorResponse): void {
    this.clearServerErrors();

    if (error?.status === 403) {
      this.isForbidden = true;
      return;
    }

    const body = error?.error;
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      this.submitError = error;
      return;
    }

    if (isStockShortageError(body)) {
      this.shortageError = body;
      return;
    }

    // Standard DRF field errors: peel off the three the form can point at, and let the
    // shared alert render whatever is left rather than dropping it.
    const rest: Record<string, unknown> = { ...body };

    if (rest['quantity']) {
      this.quantityError = firstErrorMessage(rest['quantity']);
      delete rest['quantity'];
    }

    if (rest['remarks']) {
      this.remarksError = firstErrorMessage(rest['remarks']);
      delete rest['remarks'];
    }

    if (rest['production_entry']) {
      this.productionEntryError = firstErrorMessage(rest['production_entry']);
      delete rest['production_entry'];
    }

    if (rest['warehouse']) {
      this.warehouseError = firstErrorMessage(rest['warehouse']);
      delete rest['warehouse'];
    }

    if (Object.keys(rest).length > 0) {
      this.submitError = { error: rest };
    }
  }

  private applyRemarksRequirement(): void {
    if (this.isRemarksRequired) {
      this.remarksControl.addValidators(Validators.required);
    } else {
      this.remarksControl.removeValidators(Validators.required);
    }

    this.remarksControl.updateValueAndValidity({ emitEvent: false });
  }

  private clearServerErrors(): void {
    this.shortageError = null;
    this.quantityError = null;
    this.remarksError = null;
    this.productionEntryError = null;
    this.warehouseError = null;
    this.submitError = null;
    this.isForbidden = false;
  }

  protected formatQuantity(value: string | null | undefined): string {
    return formatDecimalQuantity(value);
  }

  ngOnDestroy(): void {
    this.submitSubscription?.unsubscribe();
  }
}

/** The shortage response is the only 400 whose `quantity` is a string, not a string[]. */
function isStockShortageError(body: any): body is StockShortageError {
  return (
    typeof body?.quantity === 'string' &&
    body?.available !== undefined &&
    body?.requested !== undefined &&
    body?.shortage !== undefined
  );
}

function firstErrorMessage(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? '');

  return String(value ?? '');
}
